import { useEffect, useState } from 'react';
import { Card, CardHeader, CardBody, Avatar, Button } from '@heroui/react';

interface User {
  id: string;
  vkId: string;
  firstName: string;
  lastName: string;
  avatar?: string;
}

export function DashboardPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Получаем данные пользователя
    fetch('https://auth.tarsapp.net/api/user/me', {
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) {
          throw new Error('Not authenticated');
        }
        return res.json();
      })
      .then(data => {
        setUser(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch user:', err);
        setLoading(false);
      });
  }, []);

  const handleLogout = async () => {
    try {
      await fetch('https://auth.tarsapp.net/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      window.location.href = '/';
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="background-gradient min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Загрузка...</div>
      </div>
    );
  }

  if (!user) {
    // Если нет пользователя, перенаправляем на логин
    window.location.href = '/';
    return null;
  }

  return (
    <div className="background-gradient min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* Декоративные элементы */}
      <div className="glass-orb-1"></div>
      <div className="glass-orb-2"></div>
      <div className="glass-orb-3"></div>
      <div className="rainbow-ring"></div>
      <div className="mesh-gradient"></div>
      <div className="wave-effect"></div>

      {/* Светящиеся точки */}
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>
      <div className="sparkle"></div>

      {/* Частицы */}
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>

      {/* Основной контейнер */}
      <div className="max-w-7xl w-full relative z-10 gap-8 lg:gap-12">
        <div className="flex items-center justify-center">
          <Card
            className="max-w-md w-full backdrop-blur-xl bg-white/10 border border-white/20 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.2), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)'
            }}
          >
            <CardHeader className="flex flex-col items-center gap-3 pb-0 pt-8">
              <Avatar
                src={user.avatar}
                className="w-20 h-20"
                name={`${user.firstName} ${user.lastName}`}
              />
              <div className="relative">
                <div className="absolute inset-0 blur-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-50 animate-pulse"></div>
                <h1 className="text-2xl font-bold relative z-10 bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                  {user.firstName} {user.lastName}
                </h1>
              </div>
            </CardHeader>

            <CardBody className="flex flex-col gap-4 px-8 py-6">
              <div className="text-center text-gray-300 space-y-2">
                <p className="text-sm">VK ID: {user.vkId}</p>
                <p className="text-sm opacity-75">User ID: {user.id}</p>
              </div>

              <div className="mt-4 space-y-3">
                <p className="text-center text-gray-400 text-xs">
                  Вы успешно авторизованы в системе
                </p>
                <Button
                  onClick={handleLogout}
                  color="danger"
                  variant="flat"
                  className="w-full"
                >
                  Выйти
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
